import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Button } from '@penta/penta-shared';
import {
  getGroups,
  getEnabledHandlers,
  saveEnabledHandlers,
  resetAllMocks,
  getHandlerId,
  getHandlerDescription,
  applyMockState,
} from '../utils/mockManager';
import { isDevelopmentModeEnabled } from '../utils/envUtils';
import { worker } from '../initMocks';
import { RequestHandler } from 'msw';
import styles from './devtools.module.scss';

// Only load when mocks are enabled
export const Route = createFileRoute('/devtools')({
  beforeLoad: () => {
    if (!isDevelopmentModeEnabled()) {
      throw notFound();
    }
  },
  component: DevTools,
});

function DevTools() {
  const [enabledIds, setEnabledIds] = useState<Set<string>>(new Set());
  const [groups, setGroups] = useState<Record<string, RequestHandler[]>>({});

  useEffect(() => {
    // Only available when mocks are enabled
    if (!isDevelopmentModeEnabled()) {
      return;
    }

    // Load initial state
    setEnabledIds(getEnabledHandlers());
    setGroups(getGroups());
  }, []);

  const handleToggle = (id: string, checked: boolean) => {
    const newSet = new Set(enabledIds);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    updateState(newSet);
  };

  const updateState = (newSet: Set<string>) => {
    setEnabledIds(newSet);
    saveEnabledHandlers(newSet);
    applyMockState(worker);
  };

  const handleGroupToggle = (
    groupName: string,
    handlers: RequestHandler[],
    checked: boolean,
  ) => {
    const newSet = new Set(enabledIds);
    handlers.forEach((h) => {
      const id = getHandlerId(h);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
    });
    updateState(newSet);
  };

  const handleReset = () => {
    resetAllMocks(); // This reloads the page
  };

  const handleDisableAll = () => {
    const emptySet = new Set<string>();
    updateState(emptySet);
  };

  const handleEnableAll = () => {
    const allSet = new Set<string>();
    const allHandlers = Object.values(groups).flat();
    allHandlers.forEach((h) => allSet.add(getHandlerId(h)));
    updateState(allSet);
  };

  return (
    <div
      className={classNames(styles.container, 'theme-root')}
      data-theme="dark"
    >
      <div>
        <div className={styles.header}>
          <h1>API Mock DevTools</h1>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              size="sm"
              className={styles.buttonInverse}
              onClick={handleEnableAll}
              title="Switch all endpoints to Mock API"
              data-testid="devtools-enable-all"
            >
              Enable All (Mock)
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className={styles.buttonInverse}
              onClick={handleDisableAll}
              title="Switch all endpoints to Live API"
              data-testid="devtools-disable-all"
            >
              Disable All (Go Live)
            </Button>
            <Button
              variant="primary"
              size="sm"
              className={styles.buttonWhite}
              onClick={handleReset}
              title="Reset to default mock state"
              data-testid="devtools-reset"
            >
              Reset to Defaults
            </Button>
          </div>
        </div>

        <div className={styles.content}>
          <p>
            Control which API endpoints are mocked (checked) vs running against
            the live backend (unchecked). Changes are applied immediately and
            persisted.
          </p>

          <div className={styles.groups}>
            {Object.entries(groups).map(([groupName, groupHandlers]) => {
              const enabledCount = groupHandlers.filter((h) =>
                enabledIds.has(getHandlerId(h)),
              ).length;
              const isAllChecked = enabledCount === groupHandlers.length;
              const isIndeterminate =
                enabledCount > 0 && enabledCount < groupHandlers.length;

              return (
                <div key={groupName} className={styles.group}>
                  <div className={styles.group__header}>
                    <label className={styles['group__header--left']}>
                      <input
                        type="checkbox"
                        checked={isAllChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = isIndeterminate;
                        }}
                        onChange={(e) =>
                          handleGroupToggle(
                            groupName,
                            groupHandlers,
                            e.target.checked,
                          )
                        }
                        aria-label={`Toggle all endpoints in ${groupName}`}
                      />
                      <h2>{groupName}</h2>
                    </label>
                    <span className={styles['group__header--right']}>
                      {enabledCount} / {groupHandlers.length} active
                    </span>
                  </div>

                  <div className={styles.group__list}>
                    {groupHandlers.map((handler) => {
                      const id = getHandlerId(handler);
                      const isChecked = enabledIds.has(id);
                      const desc = getHandlerDescription(handler);
                      // Try to parse method/path for prettier display
                      // "POST /api/v1/token" -> ["POST", "/api/v1/token"]
                      const [method, ...pathParts] = desc.split(' ');
                      const path = pathParts.join(' ');
                      const isMethodKnown = [
                        'GET',
                        'POST',
                        'PUT',
                        'DELETE',
                        'PATCH',
                      ].includes(method);

                      return (
                        <div
                          key={id}
                          className={classNames(styles.handler, {
                            [styles.disabled]: !isChecked,
                          })}
                        >
                          <label className={styles.handler__label}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) =>
                                handleToggle(id, e.target.checked)
                              }
                              aria-label={`Toggle ${method}${isMethodKnown ? ` ${path}` : ''}`}
                            />
                            <div className={styles.handler__content}>
                              {isMethodKnown ? (
                                <>
                                  <span
                                    className={classNames(
                                      styles.handler__method,
                                      styles[method],
                                    )}
                                  >
                                    {method}
                                  </span>
                                  <span>{path}</span>
                                </>
                              ) : (
                                <span>{desc}</span>
                              )}
                            </div>
                          </label>
                          {!isChecked && (
                            <span className={styles.handler__badge}>LIVE</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
